import * as _ from 'underscore';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { WebClientListResourceFetcher } from '../../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { ListResult } from '../../../ts/fetchers/ListResult';
import { Filter } from '../../../ts/helpers/Filter';
import { RequestHelper } from '../../../ts/helpers/RequestHelper';
import { ResultHelper } from '../../../ts/helpers/ResultHelper';
import { ServerApiMapper } from '../../../ts/helpers/ServerModelHelper';
import { IServerUserInfo } from '../../../ts/models/IServerUserInfo';
import { Module } from '../../../ts/Module';
import { Resource } from '../../../ts/resources/Resource';
import { IResourceQuery } from '../../../ts/resources/ResourceQuery';
import { CreateLocalServerUserRequest } from '../../../ts/ServerApi/requests/CreateLocalServerUserRequest';
import {
  CreateLocalServerUserWithoutPasswordRequest,
} from '../../../ts/ServerApi/requests/CreateLocalServerUserWithoutPasswordRequest';
import { DeleteServerUsersRequest } from '../../../ts/ServerApi/requests/DeleteServerUsersRequest';
import {
  DeleteUserConnectedDevicesRequest,
  IDeleteUserConnectedDevicesParams,
} from '../../../ts/ServerApi/requests/DeleteUserConnectedDevicesRequest';
import {
  DeleteUserRemoteAgentsRequest,
  IDeleteUserRemoteAgentsParams,
} from '../../../ts/ServerApi/requests/DeleteUserRemoteAgentsRequest';
import { ForcePasswordUpdateOnSiteUsersRequest } from '../../../ts/ServerApi/requests/ForcePasswordUpdateOnSiteUsers';
import { ForcePasswordUpdateRequest } from '../../../ts/ServerApi/requests/ForcePasswordUpdateRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../../../ts/ServerApi/requests/GeneratePublicKeyRequest';
import { GetPersonalAccessTokenClientIdsParams, GetPersonalAccessTokenClientIdsRequest, GetPersonalAccessTokenClientIdsResult } from '../../../ts/ServerApi/requests/GetPersonalAccessTokenClientIdsRequest';
import { GetServerUsersRequest } from '../../../ts/ServerApi/requests/GetServerUsersRequest';
import {
  GetUserConnectedDevicesRequest,
  IGetUserConnectedDevicesParams,
  IGetUserConnectedDevicesResult,
} from '../../../ts/ServerApi/requests/GetUserConnectedDevicesRequest';
import {
  GetUserRefreshTokenCountRequest,
  IGetUserRefreshTokenCountParams,
  IGetUserRefreshTokenCountResult,
} from '../../../ts/ServerApi/requests/GetUserRefreshTokenCountRequest';
import {
  GetUserSettingsRequest,
  IGetUserSettingsParams,
  IGetUserSettingsResult,
} from '../../../ts/ServerApi/requests/GetUserSettingsRequest';
import { GetUsersSiteRolesRequest, IGetUsersSiteRolesResult } from '../../../ts/ServerApi/requests/GetUsersSiteRolesRequest';
import { ImportServerUsersFromActiveDirectoryRequest } from '../../../ts/ServerApi/requests/ImportUsersFromActiveDirectoryRequest';
import { ResetLoginRateLimitingOnSiteUsersRequest } from '../../../ts/ServerApi/requests/ResetLoginRateLimitingOnSiteUsersRequest';
import { ResetLoginRateLimitingRequest } from '../../../ts/ServerApi/requests/ResetLoginRateLimitingRequest';
import { RevokePersonalAccessTokenByClientIdParams, RevokePersonalAccessTokenByClientIdRequest } from '../../../ts/ServerApi/requests/RevokePersonalAccessTokenByClientIdRequest';
import { UpdateServerUsersAdminStatusRequest } from '../../../ts/ServerApi/requests/UpdateServerUsersAdminStatusRequest';
import {
  IUpdateServerUsersSiteMembershipResult,
  UpdateServerUsersSiteMembershipRequest,
} from '../../../ts/ServerApi/requests/UpdateServerUsersSiteMembershipRequest';
import { IConnectedDevice } from '../../../ts/ServerApi/types/IConnectedDevice';
import { FilterFieldNames, IFieldFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { IPersonalAccessToken } from '../../../ts/ServerApi/types/IPersonalAccessToken';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { ISiteRole } from '../../../ts/ServerApi/types/ISiteRole';
import { IUserActionResult } from '../../../ts/ServerApi/types/IUserActionResult';
import { NamedItemKeys } from '../../../ts/ServerApi/types/resourceParts';
import { ErrorNotifyingServer } from '../../../ts/services/ErrorNotifyingServer';
import { FetcherFactory } from '../../../ts/services/FetcherFactory';
import { SiteRolesResource } from './SiteRoles';

export class ServerUsers extends Resource<IServerUserInfo> {

  public static readonly instance = new ServerUsers(new WebClientListResourceFetcher(GetServerUsersRequest, ServerApiMapper.serverUsers));

  // returns the server user with the specified username & domainName.
  public getUserByUsernameAndDomain(username: string, domainName: string): ng.IPromise<IServerUserInfo> {
    return this.all()
      .addFilter(Filter.stringFieldEq('username', username))
      .addFilter(Filter.stringFieldEq('domainName', domainName))
      .getFirst();
  }

  // returns the server users with the specified IDs
  public getByUserIds(userIds: string[]): ng.IPromise<ListResult<IServerUserInfo>> {
    let query = this.all();
    const usersFilter = Filter.stringFieldInArray(NamedItemKeys.id, userIds);
    query = query.addFilter(usersFilter);

    return query.getCount().then((totalCount: number) => {
      return query.getSlice(0, totalCount);
    });
  }

  public getUserCountsByMaxSiteRole(baseQuery: IResourceQuery<IServerUserInfo>): ng.IPromise<_.Dictionary<number>> {
    const $q = ServiceLocator.get<ng.IQService>('$q');

    // Remove any existing max site role filter
    const filtersWithoutMaxSiteRole = _.filter(baseQuery.getFilters(), (filter: IFieldFilterClause) => {
      return !(filter.field && filter.field === FilterFieldNames.maxSiteRole);
    });
    const modifiedQuery = baseQuery.withFilters(filtersWithoutMaxSiteRole);

    const counts: _.Dictionary<number> = {};

    const promises = _.map(SiteRolesResource.instance.optionsForCurrentUser(), (maxSiteRole: string) => {
      return modifiedQuery
        .addFilter(Filter.stringFieldEq(FilterFieldNames.maxSiteRole, maxSiteRole))
        .getCount()
        .then((count: number) => {
          counts[maxSiteRole] = count;
        });
    });

    // Any site role
    promises.push(modifiedQuery.getCount().then((count: number) => { counts['Any'] = count; }));

    return $q.all(promises).then(() => counts);
  }

  public getUserSettings(username: string, domain: string): ng.IPromise<IGetUserSettingsResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const params: IGetUserSettingsParams = {
      username: username,
      domainName: domain
    };
    return ErrorNotifyingServer.sendRequest(new GetUserSettingsRequest(params));
  }

  public getUserRefreshTokenCount(username: string, domain: string): ng.IPromise<number> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const params: IGetUserRefreshTokenCountParams = {
      username: username,
      domainName: domain
    };
    return ErrorNotifyingServer.sendRequest(new GetUserRefreshTokenCountRequest(params))
      .then((result: IGetUserRefreshTokenCountResult) => result.refreshTokenCount);
  }

  public getUserConnectedDevices(userId: string): ng.IPromise<IConnectedDevice[]> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const params: IGetUserConnectedDevicesParams = {
      userId: userId
    };
    return ErrorNotifyingServer.sendRequest(new GetUserConnectedDevicesRequest(params))
      .then((result: IGetUserConnectedDevicesResult) => result.devices);
  }

  public deleteUserConnectedDevices(userId: string, deviceIds: string[]): ng.IPromise<IResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const params: IDeleteUserConnectedDevicesParams = {
      userId: userId,
      deviceIds: deviceIds
    };
    return ErrorNotifyingServer.sendRequest(new DeleteUserConnectedDevicesRequest(params));
  }

  public getPersonalAccessTokens(userId: string): ng.IPromise<IPersonalAccessToken[]> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const params: GetPersonalAccessTokenClientIdsParams = {
      userId: userId
    };
    return ErrorNotifyingServer.sendRequest(new GetPersonalAccessTokenClientIdsRequest(params))
      .then((result: GetPersonalAccessTokenClientIdsResult) => result.tokenInfos);
  }

  public deletePersonalAccessToken(userId: string, tokenName: string): ng.IPromise<IResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const params: RevokePersonalAccessTokenByClientIdParams = {
      userId: userId,
      tokenName: tokenName
    };
    return ErrorNotifyingServer.sendRequest(new RevokePersonalAccessTokenByClientIdRequest(params));
  }

  public deleteUserRemoteAgents(ownerId: string, deviceIds: string[]): ng.IPromise<IResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    const params: IDeleteUserRemoteAgentsParams = {
      ownerId: ownerId,
      deviceIds: deviceIds
    };
    return ErrorNotifyingServer.sendRequest(new DeleteUserRemoteAgentsRequest(params));
  }

  // returns the server users' roles across all sites
  public getSiteRoles(userIds: string[]): ng.IPromise<IGetUsersSiteRolesResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequests(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new GetUsersSiteRolesRequest({ ids: userIdsBlock })),
      (results: IGetUsersSiteRolesResult[]) => {
        return {
          errors: ResultHelper.combineErrors(results),
          users: ResultHelper.combine(results, (result) => result.users)
        };
      });
  }

  // site membership - add/remove server users from sites
  public updateServerUsersSiteMembership(userIds: string[], addToSiteRoles: ISiteRole[], removeFromSiteIds: string[]): ng.IPromise<IUpdateServerUsersSiteMembershipResult>
  {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests3(userIds, addToSiteRoles, removeFromSiteIds,
        (userIdsBlock, addToSiteRolesBlock, removeFromSiteIdsBlock) => new UpdateServerUsersSiteMembershipRequest({
          userIds: userIdsBlock,
          addToSiteRoles: addToSiteRolesBlock,
          removeFromSiteIds: removeFromSiteIdsBlock
        })),
        (results: IUpdateServerUsersSiteMembershipResult[]) => {
          let res = ResultHelper.combine(results, (result) => result.results);
          let err = ResultHelper.combineErrors(results);
          return {
            results: res,
            errors: err ? err : []
          };
      });
  }

  // site membership - change these users' server admin status
  public updateServerAdminStatus(userIds: string[], admin: boolean): ng.IPromise<IResult>
  {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new UpdateServerUsersAdminStatusRequest({
        userIds: userIdsBlock,
        admin: admin,
      })),
      ResultHelper.combineResults);
  }

  // force a password update on the users provided
  public forcePasswordUpdate(userIds: string[]): ng.IPromise<IUserActionResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public forcePasswordUpdateOnSiteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ForcePasswordUpdateOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the users provided
  public resetLoginRateLimiting(userIds: string[]): ng.IPromise<IUserActionResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingRequest({ systemUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // force a password update on the site users provided
  public resetLoginRateLimitingOnSiteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new ResetLoginRateLimitingOnSiteUsersRequest({ siteUserIds: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // delete users from server
  public deleteUsers(userIds: string[]): ng.IPromise<IUserActionResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(userIds, (userIdsBlock) => new DeleteServerUsersRequest({ ids: userIdsBlock })),
      ResultHelper.combineUserResults);
  }

  // import users from active directory to server
  public importUsersFromActiveDirectory(usernames: string[]): ng.IPromise<IResult> {
    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(usernames,
        (usernamesBlock) => new ImportServerUsersFromActiveDirectoryRequest({usernames: usernamesBlock})),
      ResultHelper.combineImportUserResults);
  }

  // create a new local server user
  public createLocalServerUser(
    username: string,
    displayName: string,
    email: string,
    password: string): ng.IPromise<IResult> {

    const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
    return ErrorNotifyingServer.sendRequestAndDelay(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;
        const encryptedPassword = rsa.encrypt(password, key);
        const createLocalServerUserRequest = new CreateLocalServerUserRequest({
          username: username,
          displayName: displayName,
          email: email,
          encryptedPassword: encryptedPassword,
          keyId: keyId
        });
        return ErrorNotifyingServer.sendRequestAndDelay(createLocalServerUserRequest);
      });
  }

    // create a new local server user when SAML or SiteSAML is enabled
  public createLocalServerUserWithoutPassword(
    username: string,
    displayName: string,
    email: string): ng.IPromise<IResult> {
        const ErrorNotifyingServer = ServiceLocator.get<ErrorNotifyingServer>('ErrorNotifyingServer');
        const createLocalServerRequest = new CreateLocalServerUserWithoutPasswordRequest({
          username: username,
          displayName: displayName,
          email: email
        });
        return ErrorNotifyingServer.sendRequestAndDelay(createLocalServerRequest);
  }
}

export default ServerUsers.instance;
